import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, Channel, ConfirmationComponent, Integration, IntegrationHealth, IntegrationService, OpenAIAssistant, OpenAIIntegration, openAIAssistantNeedsSetup } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';

/**
 * OpenAI agents of one integration.
 *
 * Agents are read from `integration.metadata.assistants` — since the OpenAI Assistants API
 * shutdown (2026-08-26) the agent configuration (model + instructions) lives in Trendmulti.
 * Entries created on the old API (`asst_…`) have no model/instructions and are flagged
 * "needs setup" until someone edits them.
 */
@Component({
    selector: 'app-agent-openai-assistants',
    templateUrl: './agent-openai-assistants.component.html',
    styleUrls: ['./agent-openai-assistants.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentOpenAIAssistantsComponent extends AbstractComponent implements OnInit {

  integration: Integration;
  assistants: OpenAIAssistant[] = [];
  channels: Channel[] = [];
  selectedChannel: Channel;
  resettingHealth = false;

  breadcrumb = [
    { href: '/agents/openai', label: this.t('agents.openaiAssistants.breadcrumbOpenaiIntegrations') },
    { label: this.t('agents.openaiAssistants.breadcrumbAssistants') },
  ]

  constructor(
    injector: Injector,
    private integrationService: IntegrationService,
    private dialog: MatDialog,
  ) {
    super(injector);
    this.loadingContent = true;
  }

  ngOnInit() {
    const id = super.getParam('id');
    if (!!id) {
      this.getIntegrationById(id);
    }
  }

  getIntegrationById(idIntegration: string) {
    this.loadingContent = true;
    this.integrationService.getById(idIntegration)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingContent = false),
      )
      .subscribe({
        next: (integration: Integration) => {
          this.integration = integration;
          this.assistants = (<OpenAIIntegration>integration.metadata)?.assistants ?? [];
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  get health(): IntegrationHealth | undefined {
    return (<OpenAIIntegration>this.integration?.metadata)?.health;
  }

  get needsSetupCount(): number {
    return this.assistants.filter(a => openAIAssistantNeedsSetup(a)).length;
  }

  needsSetup(assistant: OpenAIAssistant): boolean {
    return openAIAssistantNeedsSetup(assistant);
  }

  healthLabelKey(): string {
    return `agents.openai.health.${this.health?.status ?? 'GENERIC'}`;
  }

  resetHealth() {
    if (!this.integration || this.resettingHealth) return;
    this.resettingHealth = true;
    this.integrationService.resetHealth(this.integration._id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.resettingHealth = false),
      )
      .subscribe({
        next: (integration: Integration) => {
          this.integration = integration;
          this.alertService.success(this.t('agents.openai.reactivateSuccess'));
        },
        error: (err) => this.alertService.error(err?.error?.message ?? this.t('agents.openai.reactivateError')),
      });
  }

  deleteAssistant(assistant: OpenAIAssistant) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('agents.openaiAssistants.deleteConfirmation', { name: assistant.name })
    });

    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.integrationService.deleteAssistant(this.integration._id, assistant.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.alertService.success(this.t('agents.openaiAssistants.removeSuccess'));
              this.getIntegrationById(this.integration._id);
            },
            error: (err) => this.alertService.error(err?.error?.message ?? this.t('agents.openaiAssistants.removeError')),
          });
      }
    });
  }

  openEditAssistantModal(assistant: OpenAIAssistant) {
    this.router.navigate([`/agents/openai/${this.integration._id}/assistant/${assistant.id}/edit`]);
  }

  openCreateAssistantModal() {
    this.router.navigate([`/agents/openai/${this.integration._id}/assistant/create`]);
  }

  getChannelsByIdAssistant(idAssistant: string): string {
    const assistant = this.assistants.find((a: OpenAIAssistant) => a.id === idAssistant);
    return (assistant?.channels?.map((c: any) => c.name) || []).join(', ');
  }

}
